<template>
  <v-menu-wrapper
    v-model="menuOpen"
    offset-y
    min-width="auto"
    :nudge-top="nudgeTop"
    :close-on-content-click="false"
    v-bind="mergedMenuConfig"
  >
    <template
      #activator="{ on, attrs }"
    >
      <div
        ref="activatorRef"
        class="datetime-picker-activator"
        :class="{
          'datetime-picker-activator--editing': editMode && !createMode && !disabled,
          'datetime-picker-activator--enabled': !disabled,
          'datetime-picker-activator--disabled': disabled
        }"
        v-bind="attrs"
        v-on="on"
        @mousedown="calculateNudge"
      >
        <text-field
          :value="displayValue"
          :use-field-label="useFieldLabel"
          :config="mergedTextFieldConfig"
          :disabled="disabled"
          :edit-mode="editMode"
          :create-mode="createMode"
          :validation-errors="validationErrors"
          :custom-field-key="customFieldKey"
          :collection-key="collectionKey"
          :collection-index="collectionIndex"
          :update-data="updateData"
          :update-validation-errors="updateValidationErrors"
          v-bind="textFieldProps"
          @append-click="openMenu"
          @clear-validation-errors="$emit('clear-validation-errors', $event)"
          @input="onTextFieldInput"
        />
      </div>
    </template>
    <datetime-picker
      :value="value"
      :field="mergedPickerField"
      @input="onPickerInput"
      @close="menuOpen = false"
      @dblclick:date="onDateDblClick"
    />
  </v-menu-wrapper>
</template>

<script setup>
import { ref, computed } from 'vue'
import { dateService, FORMATS } from '@/global/services/date'
import DatetimePicker from '@/global/components/fields/DatetimePicker.vue'
import { resolveDatetimeAppend } from '@/global/components/fields/datetimeAppend'
import TextField from '@/global/components/fields/TextField.vue'
import VMenuWrapper from '@/global/components/vuetifyComponentsWrappers/VMenuWrapper.vue'

const props = defineProps({
  value: {
    type: String,
    default: null
  },
  label: {
    type: String,
    default: null
  },
  // Left unset, the field gets the default append image (see datetimeAppend.js).
  // Pass a mdi name for a font icon instead, or '' for no adornment at all.
  appendIcon: {
    type: String,
    default: null
  },
  // Overrides the default append image: any ImageField props
  // (`{ src, width, height, ... }`), or `false` for no image.
  appendImage: {
    type: [Object, Boolean],
    default: null
  },
  useFieldLabel: {
    type: Boolean,
    default: true
  },
  displayFormat: {
    type: String,
    default: () => FORMATS.datetimeCompact
  },
  // Source format of `value`, when it isn't a plain ISO datetime string (e.g. a bare 'HH:mm' time).
  // Left unset, the behavior is unchanged: `value` is formatted directly via `displayFormat`.
  valueFormat: {
    type: String,
    default: null
  },
  pickerField: {
    type: Object,
    default: () => ({})
  },
  textFieldConfig: {
    type: Object,
    default: () => ({})
  },
  textFieldProps: {
    type: Object,
    default: () => ({})
  },
  menuConfig: {
    type: Object,
    default: () => ({})
  },
  disabled: {
    type: Boolean,
    default: false
  },
  editMode: {
    type: Boolean,
    default: false
  },
  createMode: {
    type: Boolean,
    default: false
  },
  validationErrors: {
    type: Object,
    default: () => ({})
  },
  customFieldKey: {
    type: String,
    default: ''
  },
  collectionKey: {
    type: String,
    default: ''
  },
  collectionIndex: {
    type: Number,
    default: 0
  },
  updateData: {
    type: Function,
    default: () => {}
  },
  updateValidationErrors: {
    type: Function,
    default: () => {}
  },
  applyOnDblClick: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['input', 'clear-validation-errors', 'dblclick:date'])

const PICKER_HEIGHT = 460

const menuOpen = ref(false)
const nudgeTop = ref(0)
const activatorRef = ref(null)

function calculateNudge () {
  if (!activatorRef.value) return
  const rect = activatorRef.value.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom
  nudgeTop.value = Math.max(0, PICKER_HEIGHT - spaceBelow + 8)
}

function openMenu () {
  if (props.disabled) return
  calculateNudge()
  menuOpen.value = true
}

function onDateDblClick (instance) {
  if (props.applyOnDblClick) {
    instance.applyDate()
  }
  emit('dblclick:date', instance)
}

// The activator is readonly, so TextField's own typing-based
// `clear-validation-errors` never fires here. Picking a value (or clearing it)
// must therefore dismiss any backend validation error on the field itself -
// mirroring FieldsRenderer, which clears on picker close.
function onPickerInput (value) {
  emit('input', value)
  emit('clear-validation-errors', props.customFieldKey)
}

// The activator's `value` is one-way (bound to `displayValue`), so its own clear
// icon (on by default in TextField, and rendered here because `forceEditStyle`
// counts the activator as editable while the form is) would visually clear the
// input without this actually reaching `v-model` - forward it so clearing works.
function onTextFieldInput (value) {
  if (!value) {
    emit('input', null)
    emit('clear-validation-errors', props.customFieldKey)
  }
}

const displayValue = computed(() => {
  if (!props.value) return null

  if (props.valueFormat) {
    return props.valueFormat === props.displayFormat
      ? props.value
      : dateService.formatCustom(props.value, props.valueFormat, props.displayFormat)
  }

  return dateService.format(props.value, props.displayFormat)
})

const mergedPickerField = computed(() => ({
  // A date-only picker shouldn't default to a format that still carries a
  // time component - that's how a "00:00" ends up tacked onto the value.
  format: props.pickerField?.subType === 'date' ? FORMATS.isoDate : FORMATS.isoDatetime,
  ...props.pickerField
}))

// Image or icon, resolved from the two props - see datetimeAppend.js. A
// `textFieldConfig` carrying its own `appendIcon`/`append` still wins, since it
// is spread over this below.
const appendAdornment = computed(() =>
  resolveDatetimeAppend({ appendIcon: props.appendIcon, appendImage: props.appendImage })
)

const mergedTextFieldConfig = computed(() => ({
  ...appendAdornment.value,
  readonly: true,
  label: props.label,
  // The activator is always `readonly` (the value is picked via the menu, never
  // typed), so TextField's backend-validation gate — which needs an identifiable
  // key and an "editable" field — would otherwise never render an error on it.
  // `key` gives TextField something to match the error against (it also matches
  // on `customFieldKey`), and `forceEditStyle` opens that gate while editing.
  // `useDefaultEditStyle: false` leaves the green "editing" look to this
  // component's own activator styling instead of doubling it. A caller-supplied
  // `textFieldConfig` still overrides any of these.
  key: props.customFieldKey || undefined,
  forceEditStyle: true,
  useDefaultEditStyle: false,
  ...props.textFieldConfig
}))

const mergedMenuConfig = computed(() => ({
  disabled: props.disabled,
  ...props.menuConfig
}))
</script>

<style scoped>
.datetime-picker-activator,
.datetime-picker-activator ::v-deep input {
  cursor: pointer;
}

.datetime-picker-activator--disabled,
.datetime-picker-activator--disabled ::v-deep input {
  cursor: default !important;
}

.datetime-picker-activator--editing ::v-deep .v-text-field--outlined fieldset {
  border-color: #65AD45 !important;
}

.datetime-picker-activator--editing ::v-deep .v-input__slot {
  background-color: #F7FEE7 !important;
}

.datetime-picker-activator--enabled ::v-deep .text-field--readonly .v-input__control > .v-input__slot > .v-text-field__slot > input {
  color: #111827 !important;
  font-weight: 500 !important;
}
</style>
